/**
 * nested-carousel-group plugin
 *
 * Synchronizes the autoplay of every `.nested-swiper` instance that shares
 * a common `[data-nested-carousel-group]` ancestor, so they advance in
 * unison instead of fighting each other for attention. Designed for
 * `sections/v3-plans-with-cards.liquid`: each card's nested carousel rotates
 * its own slides, and previously each card autoplayed independently — the
 * resulting visual noise was the original motivation for this whole refactor.
 *
 * Behavior (the "option C" the user picked when we discussed trade-offs):
 *
 *   - Group members rotate in lockstep. A SINGLE setInterval calls
 *     `slideNext()` on every autoplay member at every tick. Swiper's native
 *     per-instance autoplay is stopped to avoid drift between members.
 *
 *   - Unified delay = the smallest configured delay across autoplay members.
 *     We pick the smallest so the group's cadence never feels slower than
 *     any individual carousel's authoring intent.
 *
 *   - Hover-pause: while the cursor is over any member's card, the
 *     coordinator pauses. When the cursor leaves every card, the coordinator
 *     resumes after a 200ms grace period (avoids flicker when the cursor
 *     transits between adjacent cards).
 *
 *   - Interaction-stop: pointerdown / touchstart on any member's card stops
 *     the coordinator PERMANENTLY for the rest of the page session. Matches
 *     the user's spec: "when a user interacts with or starts swiping on one
 *     they all stop autoplaying in unison." Each carousel keeps its own
 *     state — swiping one moves only that one; the group just stops driving.
 *
 * Markup contract:
 *
 *   <div data-nested-carousel-group="true">              <!-- group container -->
 *     <div data-nested-carousel-parent="true">           <!-- one card per member -->
 *       <div class="swiper-section"
 *            data-nested-carousel="true"
 *            data-autoplay="true"                        <!-- enrolls in autoplay -->
 *            data-autoplay-delay="3000">                 <!-- per-member delay -->
 *         <div class="swiper nested-swiper">...</div>
 *       </div>
 *     </div>
 *     ... more cards ...
 *   </div>
 *
 * The plugin only activates if (a) at least two `.nested-swiper` instances
 * share the same group container, AND (b) at least one of them has
 * `data-autoplay="true"`. Outside those conditions, individual carousels
 * keep behaving as they did before this plugin existed.
 *
 * Coordination with other plugins:
 *
 *   - `runsAfter: ['nested-carousel']` is declared for documentation. The
 *     group plugin reads swiper instances at `afterAllInit`, so it doesn't
 *     race nested-carousel's per-swiper setup, but the dependency is real
 *     in spirit (group operates on swipers that nested-carousel configured).
 *
 *   - The `autoplay` plugin runs first and configures Swiper's native
 *     autoplay on each member. We stop that native autoplay during the
 *     group startup phase. If the group later tears down (last member
 *     destroyed, or all autoplay flags removed), members fall back to
 *     their independent native autoplay state — but in practice the group
 *     lives as long as the section does.
 */

const groupsByContainer = new WeakMap();

const GROUP_ATTR_SELECTOR = '[data-nested-carousel-group]';
const CARD_ATTR_SELECTOR = '[data-nested-carousel-parent="true"]';
const RESUME_GRACE_MS = 200;

function findGroupContainer(swiper) {
  return swiper?.el?.closest(GROUP_ATTR_SELECTOR) || null;
}

function findMemberCard(swiper) {
  return swiper?.el?.closest(CARD_ATTR_SELECTOR) || swiper?.el || null;
}

/**
 * Resolve the autoplay delay for a member section, used to compute the
 * group's unified cadence (smallest delay across members).
 *
 * Resolution order matches autoplay.js's `readDelay` for nested carousels:
 *   1. `data-autoplay-delay` if present
 *   2. `data-speed` (the legacy nested-carousel pattern of reusing the
 *      transition speed as the autoplay delay)
 *   3. fallback 5000ms
 *
 * Returns `Infinity` when no section is available so that this member can't
 * artificially shrink the group delay.
 *
 * Exported for unit-test access; not part of the plugin's runtime surface.
 */
export function readDelay(memberSection) {
  const data = memberSection?.dataset;
  if (!data) return Infinity;
  if (data.autoplayDelay) return parseInt(data.autoplayDelay, 10);
  if (data.speed) return parseInt(data.speed, 10);
  return 5000;
}

function getOrCreateGroup(container) {
  let group = groupsByContainer.get(container);
  if (group) return group;

  group = {
    container,
    members: [],            // every nested swiper in this group
    autoplayMembers: [],    // subset whose section has data-autoplay="true"
    interval: null,
    paused: false,
    stopped: false,
    hoverCount: 0,
    delay: Infinity,        // smallest delay across autoplay members
    listenersByCard: new WeakMap(),
    pendingStart: false,
  };
  groupsByContainer.set(container, group);
  return group;
}

function pauseGroup(group) {
  if (group.interval == null) return;
  clearInterval(group.interval);
  group.interval = null;
  group.paused = true;
}

function resumeGroup(group) {
  if (group.stopped) return;
  if (group.interval != null) return;
  if (!Number.isFinite(group.delay) || group.delay <= 0) return;
  if (group.autoplayMembers.length === 0) return;

  group.paused = false;
  group.interval = setInterval(() => advanceGroup(group), group.delay);
}

function advanceGroup(group) {
  for (const m of group.autoplayMembers) {
    if (!m || m.destroyed) continue;
    if (typeof m.slideNext !== 'function') continue;
    try {
      m.slideNext();
    } catch (err) {
      console.warn('nested-carousel-group: slideNext failed', err);
    }
  }
}

function stopGroup(group) {
  if (group.interval != null) {
    clearInterval(group.interval);
    group.interval = null;
  }
  group.stopped = true;
}

function startGroup(group) {
  if (group.stopped) return;
  if (group.members.length < 2) return;
  if (group.autoplayMembers.length === 0) return;

  // Disable Swiper's native autoplay on each member; the coordinator drives
  // them now. Members without `swiper.autoplay` (data-autoplay !== 'true')
  // are left untouched.
  for (const m of group.autoplayMembers) {
    if (m?.autoplay && typeof m.autoplay.stop === 'function') {
      try {
        m.autoplay.stop();
      } catch (err) {
        console.warn(
          'nested-carousel-group: failed to stop native autoplay',
          err
        );
      }
    }
  }

  resumeGroup(group);
}

function attachInteractionListeners(group, swiper) {
  const card = findMemberCard(swiper);
  if (!card) return;
  if (group.listenersByCard.has(card)) return; // already wired this card

  const onPointerDown = () => stopGroup(group);
  const onMouseEnter = () => {
    group.hoverCount += 1;
    pauseGroup(group);
  };
  const onMouseLeave = () => {
    group.hoverCount = Math.max(0, group.hoverCount - 1);
    if (group.hoverCount > 0) return;
    if (group.stopped) return;
    setTimeout(() => {
      if (group.hoverCount === 0) resumeGroup(group);
    }, RESUME_GRACE_MS);
  };

  // `pointerdown` covers mouse, touch, and pen on every browser the theme
  // targets. Previously we ALSO bound Swiper's own `touchStart` event for
  // mobile, but pointerdown fires on touch begin via the unified Pointer
  // Events spec — so the swiper.on('touchStart') was a redundant second
  // path to the same `stopGroup(group)` call. Removed for two reasons:
  //   1. The asymmetry was a footgun: detachInteractionListeners didn't
  //      mirror the swiper.on('touchStart') binding. Swiper.destroy() did
  //      eventually clean it up, but a teardown that doesn't unwind in the
  //      same shape it wound is a future-bug magnet.
  //   2. `stopGroup` is idempotent, so the duplicate firing was a no-op,
  //      but log readers tracing the state machine had to reason about
  //      both paths.
  // If a future browser ships without Pointer Events (none on the support
  // matrix today), revisit.
  card.addEventListener('pointerdown', onPointerDown, { passive: true });
  card.addEventListener('mouseenter', onMouseEnter);
  card.addEventListener('mouseleave', onMouseLeave);

  group.listenersByCard.set(card, { onPointerDown, onMouseEnter, onMouseLeave });
}

function detachInteractionListeners(group, swiper) {
  const card = findMemberCard(swiper);
  if (!card) return;
  const handlers = group.listenersByCard.get(card);
  if (!handlers) return;
  card.removeEventListener('pointerdown', handlers.onPointerDown);
  card.removeEventListener('mouseenter', handlers.onMouseEnter);
  card.removeEventListener('mouseleave', handlers.onMouseLeave);
  group.listenersByCard.delete(card);
}

function removeMember(group, swiper) {
  const memIdx = group.members.indexOf(swiper);
  if (memIdx !== -1) group.members.splice(memIdx, 1);
  const apIdx = group.autoplayMembers.indexOf(swiper);
  if (apIdx !== -1) group.autoplayMembers.splice(apIdx, 1);

  detachInteractionListeners(group, swiper);

  // If we lose enough members that the group can no longer act as a group,
  // tear the coordinator down. The remaining lone member (if any) keeps its
  // own independent autoplay — Swiper would have already started it on
  // construction, and we restart it here for safety.
  if (group.members.length < 2 || group.autoplayMembers.length === 0) {
    if (group.interval != null) {
      clearInterval(group.interval);
      group.interval = null;
    }
    for (const m of group.autoplayMembers) {
      if (m?.autoplay && typeof m.autoplay.start === 'function') {
        try { m.autoplay.start(); } catch (_err) { /* swallow */ }
      }
    }
  }
}

export const nestedCarouselGroup = {
  name: 'nested-carousel-group',
  runsAfter: ['nested-carousel'],

  matches(el) {
    return el.classList.contains('nested-swiper');
  },

  afterAllInit(swiper) {
    const container = findGroupContainer(swiper);
    if (!container) return;

    const memberSection = swiper.el.closest('.swiper-section');
    if (memberSection?.dataset?.nestedCarousel !== 'true') return;

    const group = getOrCreateGroup(container);
    if (group.members.includes(swiper)) return; // defensive against double-fire
    group.members.push(swiper);

    if (memberSection.dataset.autoplay === 'true') {
      group.autoplayMembers.push(swiper);
      const memberDelay = readDelay(memberSection);
      if (memberDelay > 0 && memberDelay < group.delay) {
        group.delay = memberDelay;
      }
    }

    attachInteractionListeners(group, swiper);

    // Defer the coordinator start until every nested swiper in the group
    // has registered. afterAllInit fires per-swiper synchronously inside
    // `_initOrDestroyAll`'s pass 2 loop, so a queued microtask runs after
    // the loop completes and every member is in the group.
    if (!group.pendingStart) {
      group.pendingStart = true;
      queueMicrotask(() => {
        group.pendingStart = false;
        if (group.interval != null) return; // already running (resumed early)
        startGroup(group);
      });
    }
  },

  onDestroy(swiper) {
    const container = findGroupContainer(swiper);
    if (!container) return;
    const group = groupsByContainer.get(container);
    if (!group) return;
    removeMember(group, swiper);
  },
};

export default nestedCarouselGroup;
